import axios from 'axios'
import { camelizeKeys, decamelizeKeys } from 'humps'
import { toast } from 'react-toastify'
import { last } from 'lodash'
import { authorizedRequest, CancelToken } from '../utils/authUtils'

const generateUuid = require('uuid/v4')

export const instance = axios.create({
  transformResponse: [...axios.defaults.transformResponse, camelizeKeys],
})

instance.interceptors.request.use((config) => {
  const reqSource = CancelToken.source()

  if (!authorizedRequest(config?.url)) {
    reqSource.cancel('Invalid Token')
  }

  config.headers.Authorization = `Bearer ${localStorage.getItem('accessToken')}`
  config.baseURL = process.env.REACT_APP_BASE_URL

  return config
}, (error) => Promise.reject(error))

instance.interceptors.response.use((res) => {
  if (res?.config?.url === '/external/order_mutations') {
    const applied = res?.data?.orderMutations?.[0]?.applied
    const friendlyMessage = res?.data?.orderMutations?.[0]?.friendlyMessage
    const applicationMessage = res?.data?.orderMutations?.[0]?.applicationMessage
    const errorMessage = friendlyMessage ?? applicationMessage
    if (applied) {
      if (errorMessage) {
        toast.error(errorMessage)
      } else {
        const mutationType = last(res?.data?.orderMutations?.[0]?.type?.split('::'))
        toast.success(`${mutationType} was Successful`, { autoClose: 2500 })
      }
    } else {
      toast.error(errorMessage ?? 'Order Mutation Failed', { autoClose: false })
    }

    if (res?.request?.status !== 200 && res?.request?.status !== 201) {
      toast.error('Something went wrong', { autoClose: false })
    }
  }

  return res
}, (error) => {
  if (error?.message === "Network Error") {
    toast.error("Network Error", { autoClose: false })
  }

  return Promise.reject(error)
})

class Remote {
  static getEmployeeRoles = () => {
    return instance.get('/operator/employee_roles')
  }

  static getRevenueCenters = ({ venueUuid, organizationName, standUuid }) => {
    const payload = decamelizeKeys({
      params: {
        venueUuid,
        organizationName,
        uuid: standUuid
      },
    })

    return instance.get('/pos/stands', payload)
  }

  static getOrderDetails = (orderUuid) => instance.get(`/pos/orders/${orderUuid}`)

  static getOrderDeltas = (venueUuid, since_timestamp) => {
    let payload = decamelizeKeys({
      params: { venueUuid },
    })

    if (since_timestamp) {
      payload.params.since_timestamp = since_timestamp
    }
    return instance.get('/pos/data_deltas/order', payload)
  }

  static getPreOrders = ({ venueUuid, salesEventId, standUuid, standMenuUuids }) => {
    // These have to be sent in params. The backing library to Axios will not allow a body.
    const payload = decamelizeKeys({
      params: { venueUuid, standMenuUuids },
    })
    return instance.get('/pos/orders', payload)
  }

  static getCustomers = () => instance.get('/pos/customers')

  static getCustomerPayments = () => instance.get('/pos/customer/payments')

  static getOrderingPreferences = (salesEventId) => {
    const params = decamelizeKeys({
      params: { salesEventId },
    })

    return instance.get('/pos/customer/ordering_preferences', params)
  }

  static getSalesEvents = (venueUuid) => {
    const payload = decamelizeKeys({
      params: { venueUuid },
    })

    return instance.get('/pos/sales_events', payload)
  }

  static getSalesEventTypes = () => instance.get('/pos/sales_event_types')

  static getOrderTotal = (orderMenuItems, standMenuUuid, promotions) => {
    let payload = decamelizeKeys({
      affiliations: [],
      promotions,
      orderMenuItems,
      standMenuUuid,
      taxExempt: false,
    })
    payload.uuid = generateUuid()

    return instance.post('/pos/orders/total', payload)
  }

  static sendEmailJobToStadium = ({ orderUuid, emails, paymentUuid }) => {
    const payload = decamelizeKeys({
       emails,
       paymentUuid,
    })
    return instance.post(`/pos/orders/${orderUuid}/emails/PosOrderReceiptEmailJob`, payload)
  }

  static createCustomerOrderingPreference = (customerId, data) => {
    const payload = decamelizeKeys({ ordering_preferences: data })

    return instance.post(`/operator/customer/${customerId}/ordering_preferences`, payload)
  }

  static getAttendants = (organizationName) => {
    const payload = {
      params: { organizationName },
    }

    return instance.get('/operator/attendants', payload)
  }

  static getAttendantSettings = (pinNumber) => {
    const payload = {
      params: { pinNumber, venueUuid: process.env.REACT_APP_VENUE_UUID },
    }

    return instance.get('/api/v1/attendant_settings', payload)
  }

  static reopenClosedOrder = (order_uuid) => {
    const payload = {
      order_uuid,
    }

    return instance.post('/pos/order_state_transitions', payload)
  }

  static getPrinters = (venueUuid) => {
    const payload = decamelizeKeys({
      params: { venueUuid },
    })

    return instance.get('/pos/printers', payload)
  }

  static getReceiptDetails = (orderUuid) => instance.get(`/external/orders/${orderUuid}/receipt.json`)

  static updateOrder = (orderId, attributes) => {
    return instance.patch(`/pos/orders/${orderId}`, decamelizeKeys(attributes))
  }

  static getPromotions = (venueUuid, standMenuUuids) => {
    const payload = decamelizeKeys({
      params: { venueUuid, standMenuUuids },
    })

    return instance.get('/pos/promotions', payload)
  }

  static getSummaryReport = (deviceId, employeeUuid) => {
    const payload = decamelizeKeys({
      params: { deviceId, employeeUuid },
    })

    return instance.get('/pos/order_reports', payload)
  }
}

export default Remote
